import React, {useCallback, useContext, useMemo, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import styled from 'styled-components';
import {labels} from '../../../../../../../util/utils';
import {
	ListHeaderColumnTypes,
	trialBalanceType,
	entities as EntityTypes,
	guidanceType,
	non_MEST
} from '../../../../../../../util/uiconstants';
import ListHeader from '../../../../../../Main/Ares/Common/ListHeader/ListHeader';
import EntityDocuments from '../Common/EntityDocuments';
import CreateEntityForm from '../../../Common/CreateEntityForm/CreateEntityForm';
import {HelixIntegrationContext} from '../../../../../../Main/Ares/Helix/HelixIntegrationModal/index';
import ClientDataModal from '../Common/ClientDataModal/clientDataModal';
import {
	displayTooltip,
	displayDate,
	displayRiskPill,
	renderExecutionType,
	getAccountDesignation,
	convertToDecimal
} from './index';
import {displayValue} from './indexDisplayValue';
import {displayAnalyticsIcon} from './indexDisplayAnalyticsIcon';
import {useUserPreferences, useBlackListAnalyzerTypes} from '../../../../../../../util/customHooks';
import {IconoirArrowDown, IconoirArrowUp} from '@ey-xd/motif-icon';
import {isEmpty, isEqual} from 'lodash';

export default function AccountOverview({
	account,
	engagement,
	materiality,
	documentTypeId,
	body,
	onCreateDocument,
	selectedStEntityId
}) {
	const userPreferences = useUserPreferences();
	const useBlackListAnalyzerTypesValue = useBlackListAnalyzerTypes();

	const hasHelixProjects = useSelector((store) => store.configurations?.features?.hasHelixProjects || false);
	const engagementHelixProjects = useSelector((store) => store.engagementHelixProjects, shallowEqual);
	const helixConnection = engagementHelixProjects.find((x) => x.isPrimary);

	const {accountId, trialBalance} = useMemo(() => {
		const trialBalance = account.trialBalanceData?.find(
			(data) =>
				data.trialBalanceTypeId === trialBalanceType.Planning && data.stEntityId === (selectedStEntityId ?? non_MEST)
		);

		return {accountId: account.id, trialBalance};
	}, [account.id, account.trialBalanceData, selectedStEntityId]);

	const [showClientDataModal, setShowClientDataModal] = useState(false);

	const {setShowModal, setModalProps} = useContext(HelixIntegrationContext);

	const {hasHelixComponentGuidance, guidances} = useMemo(() => {
		if (body && Array.isArray(body.guidances)) {
			const guidance = body.guidances.find((guidance) => guidance.guidanceTypeId === guidanceType.HelixComponent);

			const guidances = body.guidances
				.filter((guidance) => guidance.guidanceTypeId === guidanceType.HelixComponent)
				.sort((a, b) => a.guidanceDisplayOrder - b.guidanceDisplayOrder)
				.slice(0, 6);

			return {hasHelixComponentGuidance: guidance !== undefined, guidances};
		}

		return {hasHelixComponentGuidance: false, guidances: []};
	}, [body]);

	const hideHelixComponentColumn = useMemo(() => {
		const {isMestEngagement} = engagement || {};
		const noSelectedEntity = isEqual(selectedStEntityId, 0);
		const noTrialBalance = isEmpty(trialBalance);
		const noFinancialStatements = isEmpty(trialBalance?.financialStatements);

		return (isMestEngagement && noSelectedEntity) || noTrialBalance || noFinancialStatements;
	}, [engagement, selectedStEntityId, trialBalance]);

	const showIcon = useMemo(() => {
		const hasSelectedEntity = !isEqual(selectedStEntityId, 0);
		const {isMestEngagement} = engagement || {};
		const {isPrimary} = helixConnection || {};

		return (
			(isMestEngagement ? hasSelectedEntity : true) &&
			trialBalance &&
			isPrimary &&
			hasHelixComponentGuidance &&
			!useBlackListAnalyzerTypesValue
		);
	}, [helixConnection, engagement, hasHelixComponentGuidance, selectedStEntityId, trialBalance]);

	const relatedDocuments = useMemo(() => {
		return account.relatedDocuments?.filter((document) => document.documentTypeID === documentTypeId) || {};
	}, [account.relatedDocuments, documentTypeId]);

	const displayedValues = useCallback(
		(value, financialStatement) => {
			switch (value) {
				case 'Analytics': {
					const val = convertToDecimal(financialStatement.analysisPeriodBalance, false, true);
					return displayValue(
						val,
						true,
						false,
						false,
						undefined,
						1,
						onMultipleClick,
						false,
						false,
						userPreferences,
						IconoirArrowUp,
						IconoirArrowDown
					);
				}
				case 'Change': {
					let val = convertToDecimal(financialStatement.analysisComparativeBalanceChangePercentage, false, true);
					if (val > 9999) {
						val = '9999';
					} else if (val < -9999) {
						val = '-9999';
					}
					return displayValue(
						val,
						false,
						false,
						true,
						'%',
						1,
						undefined,
						false,
						false,
						userPreferences,
						IconoirArrowUp,
						IconoirArrowDown
					);
				}
				case 'xTE': {
					const xTE =
						materiality?.teAmount && materiality?.teAmount > 0
							? convertToDecimal(financialStatement.analysisPeriodBalance / materiality?.teAmount, false, false)
							: undefined;

					let abs = Math.abs(xTE);
					if (abs > 9999.99) {
						abs = 9999.99;
					}

					return displayValue(
						abs,
						false,
						true,
						false,
						'x',
						2,
						undefined,
						false,
						false,
						userPreferences,
						IconoirArrowUp,
						IconoirArrowDown
					);
				}
			}
		},
		[materiality, onMultipleClick, userPreferences]
	);

	const renderValues = useCallback(
		(value) => {
			if (trialBalance && trialBalance.financialStatements && trialBalance.financialStatements.length > 1) {
				return displayTooltip(value, onMultipleClick);
			} else if (trialBalance && trialBalance.financialStatements && trialBalance.financialStatements.length === 1) {
				return trialBalance.financialStatements.map((financialStatement) => {
					return displayedValues(value, financialStatement);
				});
			} else {
				return <span className="noDataDash">{'-'}</span>;
			}
		},
		[displayedValues, onMultipleClick, trialBalance]
	);

	const helixComponentColumns = useMemo(
		() => [
			{
				id: 'analyticsIcon',
				type: ListHeaderColumnTypes.icon,
				title: showIcon ? labels.analytics : '',
				className: 'styledAnalyticsIcon',
				renderIcon: () =>
					showIcon ? displayAnalyticsIcon(showIcon, onAnalyticsIconClick, hasHelixProjects) : undefined
			},
			{
				id: 'analysisPeriod',
				type: ListHeaderColumnTypes.custom,
				title: !hideHelixComponentColumn
					? labels.analysisPeriodLabel.replace('{0}', displayDate(trialBalance?.analysisPeriodDate, userPreferences))
					: '',
				className: 'styledAnalytics',
				render: () => (!hideHelixComponentColumn ? renderValues('Analytics') : undefined)
			},
			{
				id: 'change',
				type: ListHeaderColumnTypes.custom,
				title: !hideHelixComponentColumn
					? labels.analysisPeriodChangeLabel.replace(
							'{0}',
							displayDate(trialBalance?.comparativePeriodDate, userPreferences)
					  )
					: '',
				className: 'styledChange',
				render: () => (!hideHelixComponentColumn ? renderValues('Change') : undefined)
			},
			{
				id: 'xTE',
				type: ListHeaderColumnTypes.custom,
				title: !hideHelixComponentColumn ? labels.xTELabel : '',
				className: 'styledXTe',
				render: () => (!hideHelixComponentColumn ? renderValues('xTE') : undefined)
			},
			{
				type: ListHeaderColumnTypes.custom,
				title: labels.designation,
				className: 'styledDesignation',
				render: () => getAccountDesignation(account?.accountTypeId)
			},
			{
				type: ListHeaderColumnTypes.custom,
				title: labels.risksLabel,
				className: 'styledRisk',
				render: () => displayRiskPill(account?.assertions)
			}
		],
		[
			account?.accountTypeId,
			account?.assertions,
			hasHelixProjects,
			hideHelixComponentColumn,
			onAnalyticsIconClick,
			renderValues,
			showIcon,
			trialBalance?.analysisPeriodDate,
			trialBalance?.comparativePeriodDate,
			userPreferences
		]
	);

	const onMultipleClick = useCallback(() => {
		setShowClientDataModal(true);
	}, []);

	const onAnalyticsIconClick = useCallback(() => {
		if (hasHelixProjects) {
			setModalProps({accountId, guidances, selectedStEntityId});
			setShowModal(true);
		}
	}, [accountId, guidances, hasHelixProjects, setModalProps, setShowModal, selectedStEntityId]);

	return (
		<StyledAccountOverview className="StyledAccountOverview">
			<ListHeader
				name={account.accountName}
				columns={helixComponentColumns}
				headerClassName={'styledAccountName'}
				headerChildren={renderExecutionType(account.executionTypeId)}
			>
				{showClientDataModal && (
					<ClientDataModal
						account={account}
						showModal={showClientDataModal}
						setShowModal={setShowClientDataModal}
						includeAnnotations={false}
						stEntityId={selectedStEntityId}
					/>
				)}
				<span className="createEntityAccount">
					<CreateEntityForm
						key={`createEntityAccount-${account.id}`}
						createLabel={labels.createDocument}
						entityId={EntityTypes.Account}
						objectIds={[account.id]}
						documentTypeId={documentTypeId}
						onCreateDocument={onCreateDocument}
						showValidations
						showDocuments={relatedDocuments && relatedDocuments.length > 0}
						selector={(store) => {
							const ids = store.entityDocuments.allIds?.filter((id) => {
								const entityDocument = store.entityDocuments.byId[id];

								return (
									entityDocument.smartEvidenceSourceId === account.id &&
									entityDocument.documentTypeId === documentTypeId
								);
							});

							return ids?.map((id) => {
								return store.entityDocuments && store.entityDocuments.byId && store.entityDocuments.byId[id];
							});
						}}
						render={({objectIds, entityForms}) => {
							return (
								<EntityDocuments
									key={`entityDocuments-${account.id}`}
									entityUId={objectIds[0]}
									documents={entityForms}
									hasRelateTask
									accountSTEntityId={selectedStEntityId}
								/>
							);
						}}
					/>
				</span>
			</ListHeader>
		</StyledAccountOverview>
	);
}

const StyledAccountOverview = styled.section`
	display: flex;
	width: 100%;
	margin-bottom: var(--px-24);
	&:last-child {
		margin-bottom: 0;
	}
	.openAnalyticDocumentation {
		border-top: var(--px-1) solid var(--neutrals-300);
		margin-top: var(--px-16);
		padding-top: var(--px-16);
		display: flex;
		align-items: center;
		width: 100%;
		.motif-icon {
			display: inline-flex;
			align-items: center;
			svg {
				width: var(--px-16) !important;
				height: var(--px-16) !important;
				fill: var(--blue-600);
				path {
					fill: var(--blue-600);
				}
			}
		}
		.openAnalyticDocumentationLabel {
			margin-left: var(--px-3);
			font-size: var(--px-14);
			font-weight: 700;
			color: var(--blue-600);
			display: inline-flex;
		}
	}
	.tooltiplabel {
		display: inline-flex;
		&.showTxt {
			cursor: pointer;
			font-size: var(--px-14);
			font-weight: 700;
			line-height: var(--px-22);
			color: var(--blue-600);
		}
	}
	.DisplayAmount {
		display: inline-flex;
		&.showTxt {
			cursor: pointer;
			font-size: var(--px-14);
			font-weight: 700;
			line-height: var(--px-22);
			color: var(--blue-600);
			&:hover {
				text-decoration: underline;
			}
		}
	}
	.executionTypeLabel {
		margin-left: var(--px-16);
		border: var(--px-1) solid var(--neutrals-900);
		border-radius: var(--px-12);
		padding: var(--px-10);
		height: var(--px-24);
		display: inline-flex;
		align-items: center;
		width: auto;
		max-width: var(--px-200);
		cursor: default;
		user-select: none;
		.executionTypeText {
			font-size: var(--px-12);
			font-weight: 400;
			line-height: var(--px-16);
			color: var(--neutrals-900);
		}
	}
	.RiskPillsContainer {
		display: inline-flex;
		margin-right: var(--px-24);
		height: var(--px-24);
		align-items: center;
		.motif-dropdown-portal {
			display: inline-flex;
			.motif-dropdown-trigger {
				display: inline-flex;
			}
		}
		svg {
			color: var(--neutrals-800);
			height: inherit;
		}
	}
`;
